import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Lock, Send } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/api/client';
import {
  addHardwareRequestInternalNote,
  listHardwareRequestInternalNotes,
  postHardwareRequestInternalNoteToZendesk,
  removeHardwareRequestInternalNote,
} from '@/api/hardwareRequests';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDateTime } from '@/lib/dateFormat';
import type { HardwareRequestDetail, HardwareRequestInternalNote } from '@/types/oscar';

/** The column's width. The API refuses longer rather than cutting the note. */
const MAX_LENGTH = 2000;

/**
 * Internal notes - the approvers' working notes on a request.
 *
 * <p>Anthony, 28/10/2026: <i>"The IT asset managers also need to put notes on the
 * request that the user or Zendesk does not need to see."</i> The decision note
 * is posted to the ticket as a public comment, so it cannot hold "waiting on a
 * quote" or "third laptop this branch has lost".</p>
 *
 * ⛔ APPROVERS ONLY, reading included - the same people who can decide. The
 * requester never sees these. A note goes to the Zendesk ticket only when an
 * approver asks (Marty, 29/10/2026, for CapEx reference tracking), as a PRIVATE
 * comment; the panel says so, so nobody has to wonder before typing.
 *
 * ⚠ SAVING IS NOT POSTING. The box can ask for both, but the note is saved first:
 * if Zendesk is unreachable the note is still there, and says why it is not on
 * the ticket. A note already on the ticket cannot be posted twice, and taking it
 * back here does not take the comment off the ticket - the confirmation says so.
 *
 * ⚠ APPEND-ONLY. A note can be taken back by its author or an admin; it cannot be
 * edited, because an edited note is one the audit trail no longer describes.
 */
export function InternalNotes({ request }: { request: HardwareRequestDetail }) {
  const { canApproveHardware } = useAuth();
  const queryClient = useQueryClient();
  const submissionId = request.SubmissionId;

  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<HardwareRequestInternalNote | null>(null);
  // ⛔ UNTICKED BY DEFAULT: a note is not posted unless the person said so, this time.
  const [alsoPost, setAlsoPost] = useState(false);

  const notes = useQuery({
    queryKey: ['hardware-request', submissionId, 'internal-notes'],
    queryFn: ({ signal }) => listHardwareRequestInternalNotes(submissionId, signal),
    enabled: canApproveHardware,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['hardware-request', submissionId, 'internal-notes'] });

  // Why a post did not land, in one sentence. ⚠ The note IS saved either way.
  const postProblem = (reason: string | null) =>
    `The note was saved, but it is not on the Zendesk ticket: ${reason ?? 'Zendesk did not confirm it.'} Press "Post to ticket" to try again.`;

  const add = useMutation({
    // Resolves to the reason the post did not land, or null (it landed, or was not asked for).
    mutationFn: async (): Promise<string | null> => {
      const saved = await addHardwareRequestInternalNote(submissionId, text.trim());
      if (!alsoPost) return null;
      try {
        const r = await postHardwareRequestInternalNoteToZendesk(submissionId, saved.NoteId);
        return r.Posted ? null : (r.Error ?? 'Zendesk did not confirm it.');
      } catch (e) {
        return e instanceof ApiError ? e.message : 'Zendesk could not be reached.';
      }
    },
    onSuccess: (postFailure) => {
      setText('');
      setAlsoPost(false);
      setError(postFailure === null ? null : postProblem(postFailure));
      void refresh();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not save the note.'),
  });

  const post = useMutation({
    mutationFn: (note: HardwareRequestInternalNote) =>
      postHardwareRequestInternalNoteToZendesk(submissionId, note.NoteId),
    onSuccess: (r) => {
      setError(r.Posted ? null : postProblem(r.Error));
      void refresh();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Could not post the note to the ticket.');
      void refresh();
    },
  });

  const takeBack = useMutation({
    mutationFn: (note: HardwareRequestInternalNote) =>
      removeHardwareRequestInternalNote(submissionId, note.NoteId),
    onSuccess: () => {
      setError(null);
      setRemoving(null);
      void refresh();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not take the note back.'),
  });

  // ⛔ Nothing at all for anybody who cannot decide the request.
  if (!canApproveHardware) return null;

  const list = notes.data?.Notes ?? [];
  const blank = text.trim().length === 0;

  return (
    <section className="mb-6" aria-labelledby="internal-notes-heading">
      <h2 id="internal-notes-heading" className="font-heading text-lg font-bold text-navy">
        Internal notes
      </h2>
      <p className="mt-1 flex items-start gap-2 text-sm text-darkGrey/80">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          Only IT asset managers and admins can see these, and the requester never sees them. They are
          not sent to Zendesk unless you choose to: &ldquo;Post to ticket&rdquo; puts one note on the
          ticket as an internal (private) comment.
        </span>
      </p>

      {notes.isLoading && <p className="mt-2 text-sm text-darkGrey/70">Loading the notes…</p>}

      {notes.isError && (
        <p role="alert" className="mt-2 text-sm text-red">
          Could not read the internal notes. If this keeps happening, contact Onsite IT Support.
        </p>
      )}

      {notes.data && list.length === 0 && (
        <p className="mt-2 text-sm text-darkGrey/80">No internal notes yet.</p>
      )}

      {list.length > 0 && (
        <ul className="mt-2 space-y-2">
          {list.map((n) => (
            <li key={n.NoteId} className="rounded border border-border bg-bg px-3 py-2">
              <p className="whitespace-pre-wrap text-sm text-darkGrey">{n.Body}</p>
              <p className="mt-1 flex flex-wrap items-baseline gap-x-3 text-xs text-darkGrey/70">
                <span>
                  {n.CreatedBy} · {formatDateTime(n.CreatedAtUtc)}
                </span>
                {n.PostedToZendeskAtUtc ? (
                  <span className="flex items-center gap-1">
                    <Send className="h-3 w-3" aria-hidden />
                    {`On the Zendesk ticket (internal) - posted by ${n.PostedToZendeskBy ?? 'unknown'} on ${formatDateTime(n.PostedToZendeskAtUtc)}`}
                  </span>
                ) : (
                  <button
                    type="button"
                    aria-label={`Post the note from ${n.CreatedBy} to the Zendesk ticket`}
                    disabled={post.isPending}
                    className="font-semibold text-navy hover:underline disabled:opacity-50"
                    onClick={() => {
                      setError(null);
                      post.mutate(n);
                    }}
                  >
                    Post to ticket
                  </button>
                )}
                {!n.PostedToZendeskAtUtc && n.ZendeskError && (
                  <span className="flex items-center gap-1 text-darkGrey">
                    <AlertTriangle className="h-3 w-3 text-orange" aria-hidden />
                    {`Not on the ticket: ${n.ZendeskError}`}
                  </span>
                )}
                {n.CanRemove && (
                  <button
                    type="button"
                    aria-label={`Take back the note from ${n.CreatedBy}`}
                    className="font-semibold text-navy hover:underline"
                    onClick={() => {
                      setError(null);
                      setRemoving(n);
                    }}
                  >
                    Take back
                  </button>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}

      <form
        className="mt-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!blank) add.mutate();
        }}
      >
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
            Add an internal note
          </span>
          <textarea
            className="w-full rounded border border-border px-2 py-1.5 text-sm"
            rows={3}
            value={text}
            maxLength={MAX_LENGTH}
            placeholder="e.g. waiting on a quote from Dell"
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <label className="mt-1 flex items-center gap-2 text-xs text-darkGrey/80">
          <input
            type="checkbox"
            checked={alsoPost}
            onChange={(e) => setAlsoPost(e.target.checked)}
          />
          Also post it to the Zendesk ticket as an internal note
        </label>
        <div className="mt-1 flex items-center justify-between gap-3">
          <button
            type="submit"
            disabled={blank || add.isPending}
            className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {add.isPending ? 'Saving…' : 'Save note'}
          </button>
          <span className="text-xs text-darkGrey/60">
            {text.length}/{MAX_LENGTH}
          </span>
        </div>
      </form>

      {error && !removing && (
        <p role="alert" className="mt-2 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
          {error}
        </p>
      )}

      <AlertDialog
        open={removing !== null}
        onOpenChange={(next) => {
          if (!next) setRemoving(null);
        }}
        title="Take this note back?"
        description={
          <>
            It disappears from this list. A record that it existed is kept.
            {removing?.PostedToZendeskAtUtc && <> It stays on the Zendesk ticket: a comment cannot be taken back off it from here.</>}
          </>
        }
        confirmLabel={takeBack.isPending ? 'Taking back…' : 'Take it back'}
        confirmDisabled={takeBack.isPending}
        closeOnConfirm={false}
        onConfirm={() => {
          if (removing) takeBack.mutate(removing);
        }}
      >
        {error && removing && (
          <p role="alert" className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
            {error}
          </p>
        )}
      </AlertDialog>
    </section>
  );
}
